import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

// Claude Design (19) ds-components/TimeField.jsx: a time input with a clock
// icon and a compact stepper (hour wraps 12 -> 1, minutes in 15-minute steps,
// AM/PM toggle). It opens at 6:00 PM when the field is empty and accepts a
// typed time such as "7:05 pm".
//
// The value contract is 24-hour "HH:MM" (or "" when unset), which is what the
// email and event schedule mappers store. Any saved minute (e.g. 18:10) shows
// and stays unchanged until the user edits the field.

export type TimeFieldPeriod = "AM" | "PM";

export type TimeFieldParts = {
  hour: number; // 1-12
  minute: number; // 0-59
  period: TimeFieldPeriod;
};

export type TimeFieldProps = {
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
  id?: string;
  placeholder?: string;
  error?: boolean;
  // Text shown under the field with the red border (audit 3.2 item 4).
  errorMessage?: string;
  disabled?: boolean;
  ariaDescribedBy?: string;
  // Field wrapper height or width overrides that keep a row of inputs aligned.
  className?: string;
};

const stepperMinutes = [0, 15, 30, 45];
const defaultTimeParts: TimeFieldParts = { hour: 6, minute: 0, period: "PM" };
const estimatedPanelHeight = 150;
const storedTimePattern = /^(\d{1,2}):(\d{2})$/;
// Design parseTime: "7", "7:05", "7:05 pm", "7pm", "19:05". Dots are
// removed by the input filter, so "p.m." arrives as "pm".
const typedTimePattern = /^\s*(\d{1,2})(?::([0-5]\d))?\s*(a|p)?\.?m?\.?\s*$/i;

/** Parses a stored 24-hour "HH:MM" value; returns null for anything else. */
export const parseStoredTime = (value: string): TimeFieldParts | null => {
  const match = storedTimePattern.exec(value.trim());
  if (!match) return null;
  const hour24 = Number(match[1]);
  const minute = Number(match[2]);
  if (hour24 > 23 || minute > 59) return null;
  return {
    hour: hour24 % 12 || 12,
    minute,
    period: hour24 >= 12 ? "PM" : "AM",
  };
};

/** Parses typed text such as "7:05 pm" or "19:05"; returns null when invalid. */
export const parseTypedTime = (text: string): TimeFieldParts | null => {
  const match = typedTimePattern.exec(text);
  if (!match) return null;
  const rawHour = Number(match[1]);
  const minute = match[2] ? Number(match[2]) : 0;
  const meridiem = match[3]?.toLowerCase();
  if (rawHour > 23 || minute > 59) return null;
  // "13:00 pm" is ambiguous, so an explicit AM/PM needs a 12-hour clock hour.
  if (meridiem && (rawHour === 0 || rawHour > 12)) return null;
  const period: TimeFieldPeriod = meridiem
    ? meridiem === "a" ? "AM" : "PM"
    : rawHour >= 12 ? "PM" : "AM";
  return { hour: rawHour % 12 || 12, minute, period };
};

export const formatTimeFieldLabel = ({ hour, minute, period }: TimeFieldParts): string =>
  `${hour}:${String(minute).padStart(2, "0")} ${period}`;

export const toStoredTime = ({ hour, minute, period }: TimeFieldParts): string =>
  `${String((hour % 12) + (period === "PM" ? 12 : 0)).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

const labelForValue = (value: string) => {
  const parts = parseStoredTime(value);
  return parts ? formatTimeFieldLabel(parts) : value;
};

type PanelPosition = { left: number; top: number; width: number; fontFamily: string };

type StepArrowProps = {
  direction: "up" | "down";
  label: string;
  onClick: () => void;
};

const StepArrow = ({ direction, label, onClick }: StepArrowProps) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
    className="flex h-[22px] w-full items-center justify-center rounded-[5px] border-0 bg-transparent p-0 text-[#727272] hover:bg-[#F7F7F8] hover:text-[#0A0A0A] focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40"
  >
    <svg aria-hidden="true" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d={direction === "up" ? "m18 15-6-6-6 6" : "m6 9 6 6 6-6"} />
    </svg>
  </button>
);

type StepperProps = {
  label: "Hour" | "Min";
  text: string;
  onStep: (delta: number) => void;
};

// Design Stepper: the up arrow is "Previous", the down arrow is "Next".
const Stepper = ({ label, text, onStep }: StepperProps) => (
  <div className="flex flex-col items-center gap-[2px]">
    <div className="text-[10px] font-bold uppercase leading-[normal] tracking-[.06em] text-[#727272]">{label}</div>
    <StepArrow direction="up" label={`Previous ${label}`} onClick={() => onStep(-1)} />
    <div className="min-w-[40px] text-center text-[20px] font-bold leading-[30px] text-[#0A0A0A] [font-variant-numeric:tabular-nums]" aria-live="polite">
      {text}
    </div>
    <StepArrow direction="down" label={`Next ${label}`} onClick={() => onStep(1)} />
  </div>
);

const TimeField = ({
  ariaLabel,
  value,
  onChange,
  id,
  placeholder = "H:MM AM/PM",
  error = false,
  errorMessage,
  disabled = false,
  ariaDescribedBy,
  className = "",
}: TimeFieldProps): React.ReactElement => {
  const panelId = useId();
  const errorMessageId = useId();
  const shownErrorMessage = error ? errorMessage?.trim() || "" : "";
  const describedBy = [ariaDescribedBy, shownErrorMessage ? errorMessageId : ""]
    .filter(Boolean)
    .join(" ") || undefined;
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const [draft, setDraft] = useState(() => labelForValue(value));
  const [picked, setPicked] = useState<TimeFieldParts>(defaultTimeParts);

  useEffect(() => {
    setDraft(labelForValue(value));
  }, [value]);

  useLayoutEffect(() => {
    if (!open) return undefined;

    // Portal panel: it follows the field and keeps the field's font.
    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const bounds = anchor.getBoundingClientRect();
      const width = Math.max(232, Math.min(bounds.width, 300));
      const below = window.innerHeight - bounds.bottom - 12;
      const next: PanelPosition = {
        left: Math.max(8, Math.min(bounds.left, window.innerWidth - width - 8)),
        top: below >= estimatedPanelHeight
          ? bounds.bottom + 6
          : Math.max(8, bounds.top - estimatedPanelHeight - 6),
        width,
        fontFamily: window.getComputedStyle(anchor).fontFamily,
      };
      setPosition((current) =>
        current &&
        current.left === next.left &&
        current.top === next.top &&
        current.width === next.width &&
        current.fontFamily === next.fontFamily
          ? current
          : next,
      );
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutside = (event: Event) => {
      if (!(event.target instanceof Node)) return;
      if (anchorRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    // Keyboard focus can leave without a mouse press.
    document.addEventListener("focusin", closeOnOutside);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("focusin", closeOnOutside);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const openPanel = () => {
    if (disabled) return;
    // Opening never changes the value; an empty field starts at 6:00 PM.
    setPicked(parseStoredTime(value) ?? defaultTimeParts);
    setOpen(true);
  };

  const apply = (next: TimeFieldParts) => {
    setPicked(next);
    setDraft(formatTimeFieldLabel(next));
    onChange(toStoredTime(next));
  };

  const stepHour = (delta: number) =>
    apply({ ...picked, hour: ((picked.hour - 1 + delta + 12) % 12) + 1 });

  const stepMinute = (delta: number) => {
    // A saved minute outside 00/15/30/45 steps from its nearest slot.
    const exactIndex = stepperMinutes.indexOf(picked.minute);
    const index = exactIndex >= 0 ? exactIndex : Math.round(picked.minute / 15) % stepperMinutes.length;
    apply({
      ...picked,
      minute: stepperMinutes[(index + delta + stepperMinutes.length) % stepperMinutes.length],
    });
  };

  const commitDraft = () => {
    const parts = parseTypedTime(draft);
    if (!parts) {
      // Invalid text is rejected and the last valid time is restored.
      setDraft(labelForValue(value));
      return;
    }
    const next = toStoredTime(parts);
    setPicked(parts);
    setDraft(formatTimeFieldLabel(parts));
    if (next !== value) onChange(next);
  };

  const borderColor = error ? "border-[#F0202E]" : "border-[#9E9E9E]";

  const panel = open && (
    <div
      ref={panelRef}
      id={panelId}
      role="dialog"
      aria-label={`${ariaLabel} picker`}
      className="fixed z-[5000] box-border flex items-center justify-center gap-[10px] rounded-[10px] border border-[#ECECEC] bg-white px-[12px] pb-[12px] pt-[10px]"
      style={{
        boxShadow: "0 6px 20px rgba(0,0,0,.10)",
        fontFamily: position?.fontFamily,
        left: position?.left,
        top: position?.top,
        width: position?.width ?? 232,
        visibility: position ? "visible" : "hidden",
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          setOpen(false);
          inputRef.current?.focus();
        }
      }}
    >
      <Stepper label="Hour" text={String(picked.hour)} onStep={stepHour} />
      <div className="pt-[14px] text-[20px] font-bold text-[#727272]" aria-hidden="true">:</div>
      <Stepper label="Min" text={String(picked.minute).padStart(2, "0")} onStep={stepMinute} />
      <div className="ml-[4px] flex flex-col gap-[5px] pt-[14px]">
        {(["AM", "PM"] as const).map((period) => {
          const selected = picked.period === period;
          return (
            <button
              key={period}
              type="button"
              aria-pressed={selected}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => apply({ ...picked, period })}
              className={`w-[46px] rounded-[6px] border-0 py-[7px] text-[13px] leading-[normal] focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40 ${
                selected ? "bg-[#E1A731] font-bold text-white" : "bg-[#F7F7F8] font-normal text-[#0A0A0A]"
              }`}
            >
              {period}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div ref={anchorRef} className="relative w-full">
      <div
        className={`flex h-[42px] items-center gap-[8px] rounded-[8px] border px-[12px] ${borderColor} ${
          disabled ? "cursor-not-allowed bg-gray-100" : "bg-white"
        } ${className}`}
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#727272" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-autocomplete="none"
          aria-label={ariaLabel}
          aria-invalid={error || undefined}
          aria-describedby={describedBy}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          disabled={disabled}
          value={draft}
          placeholder={placeholder}
          inputMode="text"
          maxLength={8}
          title="Enter a time as H:MM AM or H:MM PM, e.g. 6:00 PM"
          onFocus={openPanel}
          onClick={openPanel}
          onChange={(event) => setDraft(event.target.value.replace(/[^0-9:APMapm ]/g, ""))}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitDraft();
              setOpen(false);
            }
            if (event.key === "Escape") setOpen(false);
            if (event.key === "ArrowUp" || event.key === "ArrowDown") {
              event.preventDefault();
              if (!open) {
                openPanel();
                return;
              }
              stepHour(event.key === "ArrowUp" ? 1 : -1);
            }
          }}
          className="min-w-0 flex-1 border-0 bg-transparent py-[10px] text-[14px] leading-[normal] text-[#0A0A0A] outline-none placeholder:text-[#9E9E9E] disabled:cursor-not-allowed disabled:text-gray-500"
        />
        <button
          type="button"
          aria-label={`Choose ${ariaLabel.toLowerCase()}`}
          tabIndex={-1}
          disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (open) {
              setOpen(false);
              return;
            }
            inputRef.current?.focus();
            openPanel();
          }}
          className="flex shrink-0 items-center border-0 bg-transparent p-0 text-[#727272] disabled:cursor-not-allowed"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </div>
      {shownErrorMessage ? (
        <p id={errorMessageId} className="mt-[6px] text-[12px] leading-[16px] text-[#E24C4C]">
          {shownErrorMessage}
        </p>
      ) : null}
      {panel ? createPortal(panel, document.body) : null}
    </div>
  );
};

export default TimeField;
